import { confirmWarningConfig } from "@/configuration/confirmConfigurations";
import { getTokenHeader } from "@/helpers/commonHelper";
import EventWorkflowTable from "@/components/Events/EventWorkflowTable/EventWorkflowTable.vue";
import EventPreview from "@/components/Events/EventPreview/EventPreview.vue";
import EventFilter from "@/components/Events/EventFilter/EventFilter.vue";
import EventEditDialog from "@/components/Events/EventEditDialog/EventEditDialog.vue";

const env = require(`@/environments/${process.env.VUE_APP_ENV_PATH}`);
import axios from "axios";
import _ from "lodash";
import { uploadFile } from "@/helpers/fileHelper";
import moment from "moment";
import ImagePreviewButton from "@/components/Common/ImagePreviewButton/ImagePreviewButton.vue";

const defaultEvent = {
  type: "basic",
  series: {
    every: {
      weekdays_only: []
    },
    every_xth: {
      xth: "",
      weekday: "",
      xth_month: ""
    },
    irregular_dates: []
  },
  video_urls: [],
  features: [],
  license: {},
  additional_data: [{}],
  all_day: false,
  start_time: null,
  change_series: false,
  pictures: [],
  files: [],
  instructions: "",
  discount: [],
  location: null,
  location_text: "",
  operator: "",
  channels: [],
  category: null,
  additional_categories: [],
  active: true
};
const defaultFilter = {
  limit: 10,
  start: moment()
    .startOf("day")
    .toISOString(),
  sort: "start",
  sortBy: ["start"],
  page: 1,
  itemsPerPage: 10,
  sortDesc: [false],
  groupBy: ["groupStart"],
  groupDesc: [false],
  mustSort: false,
  multiSort: false,
  direction: "asc",
  query: {},
  isDefault: true,
  distinct: false
};

export default {
  name: "EventWorkflow",
  async mounted() {
    const itemsPerPage = defaultFilter.itemsPerPage;
    this.showPreviewImage = JSON.parse(
      localStorage.getItem("showPreviewImage")
    );
    this.filter.itemsPerPage = this.filter.limit = itemsPerPage;
    await this.fetchData();
  },
  data: () => ({
    overlay: false,
    showPreviewImage: false,
    regionalities: [
      { title: "regional", value: "communal" },
      { title: "lokal", value: "local" },
      { title: "überregional", value: "regional" },
      { title: "intern", value: "internal" }
    ],
    filter: {
      ...Object.assign({}, { ...defaultFilter })
    },
    previewedEvent: {},
    editedEvent: _.cloneDeep(Object.assign({}, { ...defaultEvent })),
    showPreviewDialog: false,
    showEditDialog: false,
    saving: false,
    isLoading: true,
    currentPage: 1,
    destroyList: [],
    editedEventMomentsFetching: false,
    editedEventFetching: false,
    editedEventFetchingDelayed: false
  }),
  methods: {
    async getFullEventFromListItem(item) {
      const headers = getTokenHeader();

      this.editedEventMomentsFetching = true;
      this.$store
        .dispatch("getMomentsForEvent", item._id)
        .then(() => (this.editedEventMomentsFetching = false));
      const response = await axios.get(
        `${env.events_api_client_url}event/${item._id}`,
        {
          params: {
            principal_id: this.$store.state.selectedPrincipal?.id
          },
          headers
        }
      );
      return response.data;
    },

    async editEvent(item) {
      this.addChannelDefaultFields(defaultEvent);
      let eventToEdit = {};
      let eventToEditPromise;
      this.$store.state.momentsForEvent = [];

      if (item) {
        if (item._id) {
          this.editedEventFetching = true;
          eventToEditPromise = this.getFullEventFromListItem(item);

          // Only show loading overlay if fetch takes longer than 1000ms
          const loadingTimeout = setTimeout(() => {
            this.editedEventFetchingDelayed = true;
          }, 1000);

          eventToEditPromise.finally(() => {
            clearTimeout(loadingTimeout);
            this.editedEventFetching = false;
            this.editedEventFetchingDelayed = false;
          });
        }
      } else {
        this.editedEvent = _.cloneDeep({
          ...defaultEvent,
          channels: this.config.default_channels
        });
      }

      this.toggleEditDialog(true);

      if (!eventToEditPromise) {
        return;
      }
      eventToEdit = await eventToEditPromise;
      if (eventToEdit?._id !== item?._id) {
        return;
      }

      this.editedEvent = _.assign(
        {},
        _.cloneDeep({ ...defaultEvent }),
        _.cloneDeep({ ...item }),
        _.cloneDeep({ ...eventToEdit })
      );
      this.editedEvent.start_date = item.start;
      this.editedEvent.start_time = moment(item.start).format("HH:mm");
    },
    addChannelDefaultFields(defaultEvent) {
      defaultEvent.alternative_content = {};
      this.channels.forEach(c => {
        defaultEvent.alternative_content[c.value] = {
          description: "",
          short_description: ""
        };
      });
    },
    async previewEvent(item) {
      this.previewedEvent = await this.getFullEventFromListItem(item);
      this.showPreviewDialog = true;
    },
    closePreview() {
      this.previewedEvent = null;
      this.showPreviewDialog = false;
    },
    getTypeIcon(item) {
      switch (item.type) {
        case "series":
          return "mdi-calendar-range";
        default:
          return "mdi-calendar";
      }
    },
    setShowPreviewImage() {
      this.showPreviewImage = !this.showPreviewImage;
      localStorage.setItem("showPreviewImage", this.showPreviewImage);
    },

    formatDate(date) {
      return moment(date).format("DD.MM.YYYY HH:mm");
    },
    getStartEndDate(item) {
      const { start, end } = item ?? {};
      if (start) {
        if (end) return `${this.formatDate(start)} - ${this.formatDate(end)}`;
        else return this.formatDate(start);
      }
    },
    getStartTime(item) {
      if (item.all_day) return "Ganztägig";
      else return moment(item.moment?.from ?? item.start).format("HH:mm");
    },
    getOrganizer(item) {
      const { operator } = item ?? {};
      if (operator && this.organizerById?.[operator])
        return this.organizerById[operator];
    },
    getRegionality(item) {
      const regionality = this.regionalities.find(r => item.includes(r.value));
      return regionality.title;
    },

    getLocation(item, withLocationText = true) {
      const { location } = item ?? {};
      let returnValue = "";
      if (location && this.locationsById?.[location])
        returnValue += this.locationsById[location];
      if (item.location_text && withLocationText)
        returnValue += ` ${item.location_text}`;
      return returnValue;
    },
    getLocationCity(item) {
      return (
        item?.locationObj?.[0]?.address?.city ??
        this.locations?.find(l => l._id === item.location)?.address?.city ??
        ""
      );
    },
    toggleEditDialog(value) {
      if (!value) {
        this.editedEvent = _.cloneDeep({ ...defaultEvent });
        this.$refs.editDialog?.$refs?.processingStatus?.$refs?.logOverview?.resetLogs();
        this.$refs.editDialog?.$refs?.eventEditForm?.reset();
      }
      this.showEditDialog = value;
    },
    async fetchData() {
      this.isLoading = true;
      try {
        await this.$store.dispatch("getEventsConfig");
        await this.$store.dispatch("getFeatureGroupsForEvents");
        await this.$store.dispatch("getCategoryGroupsForEvents");
        await this.$store.dispatch("getOrganizer", {
          eventList: true
        });
        await this.$store.dispatch("getAllUsers");
        const itemToEdit = this.events.find(
          e => e._id === this.$route.params.editId
        );
        if (itemToEdit) this.editEvent(itemToEdit);
        this.isLoading = false;
      } catch (error) {
        await this.$store.dispatch("createError", error);
      }
    },
    async deleteEvent(item) {
      const message =
        item.type === "series"
          ? "Achtung die ganze Serie wird gelöscht. Sind Sie sicher, dass Sie fortfahren möchten?"
          : "Sind Sie sicher, dass Sie diese Veranstaltung löschen möchten?";
      const isConfirmed = await this.$confirm(message, confirmWarningConfig);
      if (isConfirmed) {
        const headers = getTokenHeader();
        try {
          const response = await axios.delete(
            `${env.events_api_client_url}event/${item._id}`,
            { params: { principal_id: this.selectedPrincipal.id }, headers }
          );
          if (response.status === 200) {
            await this.$store.dispatch("getExternEvents", this.filter);
            await this.$store.dispatch(
              "createSuccess",
              "Veranstaltung wurde erfolgreich gelöscht!"
            );
          }
        } catch (error) {
          await this.$store.dispatch("createError", error);
        }
      }
    },
    async applyEvent(item) {
      const isConfirmed = await this.$confirm(
        "Sind Sie sicher, dass Sie diese Veranstaltung freigeben möchten?",
        confirmWarningConfig
      );
      if (isConfirmed) {
        const headers = getTokenHeader();
        await axios.get(
          `${env.events_api_client_url}externEvents/approve/${item._id}`,
          { headers }
        );
        await this.$store.dispatch("getExternEvents", {
          ...this.filter,
          page: this.currentPage
        });
      }
    },
    async declineEvent(item) {
      const isConfirmed = await this.$confirm(
        "Sind Sie sicher, dass Sie diese Veranstaltung ablehnen möchten?",
        confirmWarningConfig
      );
      if (isConfirmed) {
        const headers = getTokenHeader();
        await axios.get(
          `${env.events_api_client_url}externEvents/decline/${item._id}`,
          { headers }
        );

        await this.$store.dispatch("getExternEvents", {
          ...this.filter,
          page: this.currentPage
        });
      }
    },
    async saveChannels(item) {
      if (!item.deleted) {
        await this.updateEvent(item);
      }
    },
    async createEvent(
      event,
      successMessage = "Veranstaltung wurde erfolgreich angelegt!"
    ) {
      if (!event) event = this.editedEvent;
      const headers = getTokenHeader();
      try {
        const response = await axios.post(
          `${env.events_api_client_url}event`,
          event,
          {
            headers
          }
        );
        if (response.status === 201 || response.status === 200) {
          await this.$store.dispatch("getExternEvents", this.filter);
          await this.$store.dispatch("getMomentsForEvent", response.data._id);
          await this.$store.dispatch("createSuccess", successMessage);
          return response.data;
        }
      } catch (error) {
        if (error?.response?.data?.message === "Unexpected series")
          await this.$store.dispatch("createError", {
            text: "Serie fehlerhaft!",
            error
          });
        else await this.$store.dispatch("createError", error);
      }
    },
    async updateEvent(event) {
      if (!event) event = this.editedEvent;

      const headers = getTokenHeader();
      try {
        const response = await axios.put(
          `${env.events_api_client_url}event`,
          event,
          { params: { principal_id: this.selectedPrincipal.id }, headers }
        );
        if (response.status === 201 || response.status === 200) {
          await this.$store.dispatch("getExternEvents", {
            ...this.filter,
            page: this.currentPage
          });
          await this.$store.dispatch("getMomentsForEvent", event._id);
          await this.$store.dispatch(
            "createSuccess",
            "Veranstaltung wurde erfolgreich aktualisiert!"
          );
          return response.data;
        }
      } catch (error) {
        if (error?.response?.data?.message === "Unexpected series")
          await this.$store.dispatch("createError", {
            text: "Serie fehlerhaft!",
            error
          });
        else await this.$store.dispatch("createError", error);
      }
    },
    async applyFilter(filter) {
      if (!filter) filter = this.filter;
      this.currentPage = this.filter.page;
      this.isLoading = true;
      await this.$store.dispatch("getExternEvents", filter);
      this.isLoading = false;
    },
    async resetFilter() {
      this.filter = { ...Object.assign({}, { ...defaultFilter }) };
      await this.$store.dispatch("getExternEvents", this.filter);
    },
    applySearchText: _.debounce(async function() {
      if (this.filter.s.length >= 3 || !this.filter.s.length)
        await this.applyFilter(this.filter);
    }, 500),
    async save(closeDialog) {
      if (!this.saving) {
        this.saving = true;
        if (this.editedEvent?.start_date) {
          this.editedEvent = this.cleanUp(this.editedEvent);

          if (this.editedEvent.pictures?.length) {
            await this.editedEvent.pictures.asyncForEach(async pic => {
              if (pic && pic instanceof File) {
                const { url } = await this.uploadFile(pic, undefined, "events");
                pic.url = url;
                pic.principal_id = this.selectedPrincipal.id;
                pic.originalUrl = url;
                pic.dataUrl = "";
              }
            });
          }

          if (this.editedEvent.files?.length) {
            await this.editedEvent.files.asyncForEach(async file => {
              if (file && file instanceof File) {
                const { url } = await this.uploadFile(
                  file,
                  undefined,
                  "events"
                );
                file.url = url;
                file.principal_id = this.selectedPrincipal.id;
                file.originalUrl = url;
                file.dataUrl = "";
              }
            });
          }
          let success = false;
          if (!this.editedEvent._id) {
            this.editedEvent.principal_id = this.selectedPrincipal.id;
            const savedEvent = await this.createEvent(this.editedEvent);

            if (savedEvent) {
              this.editEvent(savedEvent);
              success = true;
            }
          } else {
            success = await this.updateEvent(this.editedEvent);
            this.editedEvent.series.resetSeries = false;
          }
          if (success) {
            this.resetChangedValueFlags();
            await this.removeFilesOnSave();
          }
          const logOverviewRef = this.$refs.editDialog?.$refs?.processingStatus
            ?.$refs?.logOverview;
          const logsFetched = logOverviewRef.logsFetched;
          logOverviewRef.resetLogs();

          if (closeDialog && success) {
            this.editedEvent = _.cloneDeep({ ...defaultEvent });
            this.toggleEditDialog(!closeDialog);
          } else if (logsFetched) {
            await logOverviewRef.getLogs();
          }
        } else
          await this.$store.dispatch("createError", {
            text: "Bitte geben Sie einen Termin für die Veranstaltung an!"
          });
        this.saving = false;
      }
    },
    async removeFilesOnSave() {
      await this.destroyList.asyncForEach(async id => {
        try {
          const headers = getTokenHeader();
          await axios.delete(
            `${env.events_api_client_url}uploads/${id}/${this.editedEvent._id}`,
            { headers }
          );
        } catch (err) {
          await this.$store.dispatch("createError", {
            ...err,
            text: "Fehler beim Dateien entfernen"
          });
        }
      });
      this.destroyList = [];
    },
    cleanUp(event) {
      if (event) {
        if (!event.operator) event.operator = null;
        event.additional_data = event.additional_data.filter(
          data => !!data.type_id
        );
        return event;
      }
    },
    uploadFile,
    resetChangedValueFlags() {
      this.editedEvent.changedStartTime = false;
      this.editedEvent.changedStartDate = false;
      this.editedEvent.changedEndsAt = false;
      this.editedEvent.changedDuration = false;
    }
  },
  computed: {
    headers() {
      const headers = [
        {
          text: "Vorschaubild",
          align: "start",
          value: "image",
          width: "10%",
          sortable: false
        },
        {
          text: "Vorschau",
          value: "preview",
          align: "start",
          width: "6%",
          sortable: false
        },
        { text: "Duplikat?", value: "isPossibleDuplicate", align: "center" },
        { text: "Titel", value: "title" },
        { text: "Typ", value: "type" },
        { text: "Start Uhrzeit", value: "startTime" },
        { text: "Veranstalter", value: "organizer" },
        { text: "Standort (Titel)", value: "locationTitle" },
        { text: "Standort (Ort)", value: "locationCity" },
        { text: "Bezug", value: "regionality" },
        { text: "Veranstaltungsduplikat", value: "duplicateEntryId" }
      ];
      // hide channels-column if empty or only web-channel
      if (
        this.config?.channels?.length &&
        this.config.channels.join("") !== "web"
      ) {
        headers.push({ text: "Kanäle", value: "channels" });
      }
      headers.push({ text: "Aktionen", value: "actions", sortable: false });
      return headers;
    },
    config() {
      return this.$store.state.eventConfig;
    },
    events() {
      return this.$store.state.externEvents;
    },
    eventCount() {
      return Number(this.$store.state.externEventCount ?? 0);
    },
    organizer() {
      return this.$store.state.organizer;
    },
    organizerById() {
      return this.$store.state.organizerById;
    },
    locations() {
      return this.$store.state.locations;
    },
    locationsById() {
      return this.$store.state.locationsById;
    },
    channels() {
      return this.$store.state.eventConfig?.standard_channels;
    },
    selectedPrincipal() {
      return this.$store.state.selectedPrincipal;
    },
    users() {
      return this.$store.state.users;
    },
    categoryGroups() {
      return this.$store.state.categoryGroupsForEvents;
    },
    categories() {
      return _.flatten(this.categoryGroups?.map(cg => cg.categories)) ?? [];
    },
    features() {
      return (
        _.flatten(
          this.$store.state.featureGroupsForEvents?.map(fg => fg.features)
        ) ?? []
      );
    },
    featureGroups() {
      return this.$store.state.featureGroupsForEvents;
    },
    groupedOrganizer() {
      const groupedOrganizer = _.groupBy(this.organizer, "address.city");
      return _.sortBy(
        Object.keys(groupedOrganizer).map(key => ({
          title: key,
          _id: key,
          sub: _.uniqBy(_.sortBy(groupedOrganizer[key], "title"), "_id")
        })),
        "title"
      );
    },
    groupedLocations() {
      const groupedLocations = _.groupBy(this.locations, "address.city");
      return _.sortBy(
        Object.keys(groupedLocations).map(key => ({
          title: key,
          _id: key,
          sub: _.sortBy(groupedLocations[key], "title")
        })),
        "title"
      );
    }
  },
  watch: {
    async selectedPrincipal() {
      await this.fetchData();
    }
  },
  components: {
    ImagePreviewButton,
    EventWorkflowTable,
    EventPreview,
    EventFilter,
    EventEditDialog
  }
};
